import { createReducer } from '@reduxjs/toolkit'

import {
  isDashboardWidget,
  isDashboardWidgetFullyLoaded,
  isWidgetSettingsLoaded,
  TWidget
} from 'types/GlobalWidget'

import * as actions from '../../actions/Widgets/actions'
import * as dashboardActions from '../../actions/Dashboards/actions'
import * as dashboardWidgetActions from '../../actions/Widgets/DashboardWidgets/actions'
import * as reportWidgetActions from '../../actions/Widgets/ReportWidgets/actions'
import * as exportTableActions from '../../actions/ExportTables/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import * as kpiOptionActions from '../../actions/KpiOptions/actions'
import * as widgetTagActions from '../../actions/WidgetTags/actions'
import * as widgetTagStatusActions from '../../actions/WidgetTagStatuses/actions'
import * as additionalSegmentActions from '../../actions/AdditionalSegments/actions'
import * as variantOptionActions from '../../actions/VariantOptions/actions'
import * as globalFilterActions from '../../actions/GlobalFilter/actions'

import { removeItem, upsertItem, upsertItems } from 'redux/utils'
import { TParsedError } from 'utils/errorParser'

export interface WidgetReducerType {
  widgets: { [id: string]: TWidget }

  // Keeps track of latest data fetch in order to not show old data.
  fetchIds: { [widgetId: string]: string | null | undefined }

  // Store HTTP errors separately from widget status
  fetchErrors: {
    [widgetId: string]: TParsedError | undefined
  }

  isCreating: boolean
  isCreatingKpiOption: boolean
}

// Helper to invalidate widget data and increment version
const invalidateWidget = (widget: TWidget): void => {
  widget.data = undefined
  widget.dataVersion = (widget.dataVersion || 0) + 1
}

// Initial state
const INITIAL_STATE: WidgetReducerType = {
  widgets: {},

  // Keeps track of latest data fetch in order to not show old data.
  fetchIds: {},

  // Store HTTP errors separately from widget status
  fetchErrors: {},

  isCreating: false,
  isCreatingKpiOption: false
}

// Reducer
const widgetsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    // tryGetOneDashboard
    .addCase(dashboardActions.getOneSuccess, (state, action) => {
      state.widgets = upsertItems(
        state.widgets,
        action.payload.widgets as TWidget[]
      )
    })

    // tryGetOnePublicDashboard & tryGetOneApiAccessDashboard
    .addCase(dashboardActions.getOnePublicSuccess, (state, action) => {
      state.widgets = upsertItems(
        state.widgets,
        action.payload.widgets as TWidget[]
      )
    })

    // tryGetOneExportTable
    .addCase(exportTableActions.getOneSuccess, (state, action) => {
      state.widgets = upsertItem(
        state.widgets,
        action.payload.widget as TWidget
      )
    })

    // tryPutFunnelStages
    .addCase(dashboardWidgetActions.putFunnelStagesSuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.id]

      if (isDashboardWidgetFullyLoaded(widget)) {
        widget.settings.segment_by.stages = payload.data
      }
    })

    // tryDeleteWidget
    .addCase(dashboardWidgetActions.destroySuccess, (state, action) => {
      const { payload } = action

      state.widgets = removeItem(state.widgets, payload.id)
    })

    // tryCreateWidget
    .addCase(dashboardWidgetActions.create, (state) => {
      state.isCreating = true
    })
    .addCase(dashboardWidgetActions.createSuccess, (state, action) => {
      const { payload } = action

      state.isCreating = false
      state.widgets[payload.data.id] = payload.data
    })
    .addCase(dashboardWidgetActions.createFailed, (state) => {
      state.isCreating = false
    })

    // tryUpdateWidget
    .addCase(actions.updateSuccess, (state, action) => {
      const { payload } = action
      const preserved = state.widgets[payload.data.id]

      state.widgets[payload.data.id] = payload.data

      const widget = state.widgets[payload.data.id]

      if (
        payload.skipFetchData &&
        isWidgetSettingsLoaded(preserved) &&
        isWidgetSettingsLoaded(widget)
      ) {
        widget.data = preserved.data
        widget.dataVersion = preserved.dataVersion

        widget.settings.date_filter.selected.from_date =
          preserved.settings.date_filter.selected.from_date
        widget.settings.date_filter.selected.to_date =
          preserved.settings.date_filter.selected.to_date

        widget.settings.comparative_period.selected.from_date =
          preserved.settings.comparative_period.selected.from_date
        widget.settings.comparative_period.selected.to_date =
          preserved.settings.comparative_period.selected.to_date
      } else {
        invalidateWidget(widget)
      }
    })

    // tryGetOneDashboardWidget, tryGetOnePublicDashboardWidget,
    // tryGetOneApiAccessDashboardWidget
    .addCase(dashboardWidgetActions.getOne, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.id]

      state.fetchIds[payload.id] = payload.fetchId
      widget.data = undefined

      // Clear any previous fetch errors for this widget
      delete state.fetchErrors[payload.id]

      if (widget.status) {
        widget.status.broken = false
      }
    })
    .addCase(dashboardWidgetActions.getOneSuccess, (state, action) => {
      const { payload } = action

      if (state.fetchIds[payload.data.id] === payload.fetchId) {
        state.widgets[payload.data.id] = payload.data
      }
    })
    .addCase(dashboardWidgetActions.getOneFailed, (state, action) => {
      const { payload } = action

      // Only update if this is the latest fetch
      if (state.fetchIds[payload.widgetId] === payload.fetchId) {
        // Store the error with status code
        state.fetchErrors[payload.widgetId] = payload.error
      }
    })

    // tryUpdateDashboardLayout
    .addCase(dashboardActions.updateWidgetLayoutSuccess, (state, action) => {
      const { payload } = action

      payload.layout.forEach(({ id, ...layout }) => {
        const widget = state.widgets[id]

        if (isDashboardWidget(widget)) {
          widget.layout = layout
        }
      })
    })

    // tryUploadWidgetImage
    .addCase(dashboardWidgetActions.uploadImageSuccess, (state, action) => {
      const { payload } = action

      state.widgets[payload.id] = payload
    })

    // tryCreateKpiOption
    .addCase(kpiOptionActions.create, (state) => {
      state.isCreatingKpiOption = true
    })
    .addCase(kpiOptionActions.createSuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.widget_id]

      state.isCreatingKpiOption = false

      if (isWidgetSettingsLoaded(widget)) {
        widget.settings.kpi_options = [...widget.settings.kpi_options, payload]
        invalidateWidget(widget)
      }
    })
    .addCase(kpiOptionActions.createFailed, (state) => {
      state.isCreatingKpiOption = false
    })

    // tryUpdateKpiOption
    .addCase(kpiOptionActions.updateSuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.data.widget_id]

      if (isWidgetSettingsLoaded(widget)) {
        widget.settings.kpi_options = widget.settings.kpi_options.map(
          (option) => (option.id === payload.data.id ? payload.data : option)
        )

        if (!payload.skipFetchData) {
          invalidateWidget(widget)
        }
      }
    })

    // tryDeleteKpiOption
    .addCase(kpiOptionActions.destroySuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.widgetId]

      if (isWidgetSettingsLoaded(widget)) {
        widget.settings.kpi_options = widget.settings.kpi_options.filter(
          (option) => option.id !== payload.id
        )

        invalidateWidget(widget)
      }
    })

    // tryCreateWidgetTag
    .addCase(widgetTagActions.createSuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.widgetId]

      if (isWidgetSettingsLoaded(widget)) {
        widget.settings.tags.selected = [
          ...widget.settings.tags.selected,
          payload.data
        ]

        invalidateWidget(widget)
      }
    })

    // tryUpdateWidgetTag
    .addCase(widgetTagActions.updateSuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.widgetId]

      if (isWidgetSettingsLoaded(widget)) {
        widget.settings.tags.selected = widget.settings.tags.selected.map(
          (tag) => (tag.id === payload.data.id ? payload.data : tag)
        )

        invalidateWidget(widget)
      }
    })

    // tryDeleteWidgetTag
    .addCase(widgetTagActions.destroySuccess, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.widgetId]

      if (isWidgetSettingsLoaded(widget)) {
        widget.settings.tags.selected = widget.settings.tags.selected.filter(
          (tag) => tag.id !== payload.id
        )

        invalidateWidget(widget)
      }
    })

    // tryCreateWidgetTagStatus
    .addCase(widgetTagStatusActions.createSuccess, (state, action) => {
      const { statusItem, tagId, widgetId } = action.payload
      const widget = state.widgets[widgetId]

      if (!isWidgetSettingsLoaded(widget)) return

      const tag = widget.settings.tags.selected.find((t) => t.id === tagId)

      if (!tag) return

      const existingIndex = tag.settings.status.items.findIndex(
        (item) => item.id === statusItem.id
      )

      if (existingIndex !== -1) {
        tag.settings.status.items[existingIndex] = statusItem
      } else {
        tag.settings.status.items.push(statusItem)
      }

      invalidateWidget(widget)
    })

    // tryUpdateWidgetTagStatus
    .addCase(widgetTagStatusActions.updateSuccess, (state, action) => {
      const { statusItem, tagId, widgetId } = action.payload
      const widget = state.widgets[widgetId]

      if (!isWidgetSettingsLoaded(widget)) return

      const tag = widget.settings.tags.selected.find((t) => t.id === tagId)

      if (!tag) return

      const itemIndex = tag.settings.status.items.findIndex(
        (item) => item.id === statusItem.id
      )

      if (itemIndex !== -1) {
        tag.settings.status.items[itemIndex] = statusItem
      }

      invalidateWidget(widget)
    })

    // tryDeleteWidgetTagStatus
    .addCase(widgetTagStatusActions.destroySuccess, (state, action) => {
      const { id, tagId, widgetId } = action.payload
      const widget = state.widgets[widgetId]

      if (!isWidgetSettingsLoaded(widget)) return

      const tag = widget.settings.tags.selected.find((t) => t.id === tagId)

      if (!tag) return

      tag.settings.status.items = tag.settings.status.items.filter(
        (item) => item.id !== id
      )

      invalidateWidget(widget)
    })

    // tryCreateAdditionalSegmentBy
    .addCase(additionalSegmentActions.createSuccess, (state, action) => {
      const segment = action.payload
      const widget = state.widgets[segment.widget_id]

      if (!isWidgetSettingsLoaded(widget)) return

      // Ersätt segment på index (samma som backend skickar)
      widget.settings.segment_by.additional[segment.index] = segment
      invalidateWidget(widget)
    })

    // tryUpdateAdditionalSegmentBy
    .addCase(additionalSegmentActions.updateSuccess, (state, action) => {
      const segment = action.payload
      const widget = state.widgets[segment.widget_id]

      if (!isWidgetSettingsLoaded(widget)) return

      // Ersätt segment på index
      widget.settings.segment_by.additional[segment.index] = segment
      invalidateWidget(widget)
    })

    // tryDeleteAdditionalSegmentBy
    .addCase(additionalSegmentActions.destroySuccess, (state, action) => {
      const { id, widgetId } = action.payload
      const widget = state.widgets[widgetId]

      if (!isWidgetSettingsLoaded(widget)) return

      // Filtrera bort och re-indexera
      widget.settings.segment_by.additional =
        widget.settings.segment_by.additional
          .filter((segment) => segment.id !== id)
          .map((segment, index) => ({ ...segment, index }))

      invalidateWidget(widget)
    })

    // tryCreateVariantOptionBy
    .addCase(variantOptionActions.createSuccess, (state, action) => {
      const column = action.payload
      const widget = state.widgets[column.widget_id]

      if (!isWidgetSettingsLoaded(widget)) return

      // the breakdowns and widget tags the api cleared arrive with
      // the refetch the invalidation triggers
      widget.settings.variant_options[column.index] = column

      invalidateWidget(widget)
    })

    // tryUpdateVariantOptionBy
    .addCase(variantOptionActions.updateSuccess, (state, action) => {
      const column = action.payload
      const widget = state.widgets[column.widget_id]

      if (!isWidgetSettingsLoaded(widget)) return

      widget.settings.variant_options[column.index] = column
      invalidateWidget(widget)
    })

    // tryDeleteVariantOptionBy
    .addCase(variantOptionActions.destroySuccess, (state, action) => {
      const { id, widgetId } = action.payload
      const widget = state.widgets[widgetId]

      if (!isWidgetSettingsLoaded(widget)) return

      // Filtrera bort och re-indexera
      widget.settings.variant_options = widget.settings.variant_options
        .filter((column) => column.id !== id)
        .map((column, index) => ({ ...column, index }))

      invalidateWidget(widget)
    })
    /* --- REPORT WIDGET ACTIONS --- */
    // tryGetOneReportWidget
    .addCase(reportWidgetActions.getOne, (state, action) => {
      const { payload } = action
      const widget = state.widgets[payload.id]

      state.fetchIds[payload.id] = payload.fetchId

      if (widget && isWidgetSettingsLoaded(widget)) {
        widget.data = undefined

        // Clear any previous fetch errors for this widget
        delete state.fetchErrors[payload.id]

        if (widget.status.broken) {
          widget.status.broken = false
        }
      }
    })
    .addCase(reportWidgetActions.getOneSuccess, (state, action) => {
      const { data, fetchId } = action.payload

      if (state.fetchIds[data.id] === fetchId) {
        state.widgets[data.id] = data
      }
    })
    .addCase(reportWidgetActions.getOneFailed, (state, action) => {
      const { payload } = action

      // Only update if this is the latest fetch
      if (state.fetchIds[payload.widgetId] === payload.fetchId) {
        // Store the error with status code
        state.fetchErrors[payload.widgetId] = payload.error
      }
    })
    .addCase(reportWidgetActions.destroySuccess, (state, action) => {
      const { id } = action.payload

      state.widgets = removeItem(state.widgets, id)
    })
    // tryEditDataReportWidget
    .addCase(reportWidgetActions.editDataPending, (state, action) => {
      const widget = state.widgets[action.payload.id]

      if (widget) {
        widget.data = undefined
      }
    })
    .addCase(reportWidgetActions.editDataFailed, (state, action) => {
      const widget = state.widgets[action.payload.id]

      if (widget) {
        widget.dataVersion = (widget.dataVersion || 0) + 1
      }
    })

    // tryUpdateReportWidget
    .addCase(reportWidgetActions.updateSuccess, (state, action) => {
      const { payload } = action

      const prevWidget = state.widgets[payload.data.id]
      const prevDataVersion = prevWidget?.dataVersion ?? 0

      state.widgets[payload.data.id] = payload.data
      const widget = state.widgets[payload.data.id]

      widget.data = undefined
      widget.dataVersion = prevDataVersion + 1
    })

    // invalidateWidgets
    .addCase(actions.invalidateWidgets, (state) => {
      Object.values(state.widgets).forEach((widget) => {
        invalidateWidget(widget)
      })
    })

    .addCase(globalFilterActions.update, (state) => {
      Object.values(state.widgets).forEach((widget) => {
        invalidateWidget(widget)
      })
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default widgetsReducer
